<template>
  <v-form
    ref="form"
    :disabled="busy || loading"
    validate-on="submit"
    @submit.prevent="submit"
  >
    <div class="text-h5 my-4">Purchase details</div>
    <v-radio-group v-model="method" inline label="Send by">
      <v-radio label="Email" :value="PurchaseDeliveryMethod.EMAIL" />
      <v-radio label="Mail" :value="PurchaseDeliveryMethod.MAIL" />
      <v-radio
        label="In person pickup"
        :value="PurchaseDeliveryMethod.PICKUP"
      />
    </v-radio-group>
    <v-radio-group v-model="target" inline label="Send to">
      <v-radio label="Me" :value="PurchaseDeliveryTarget.PURCHASER" />
      <v-radio
        label="Specify recipient"
        :value="PurchaseDeliveryTarget.RECIPIENT"
      />
    </v-radio-group>
    <template v-if="target === PurchaseDeliveryTarget.RECIPIENT">
      <v-text-field
        v-model="first"
        label="First name"
        maxlength="100"
        :rules="[required]"
      />
      <v-text-field
        v-model="last"
        label="Last name"
        maxlength="100"
        :rules="[required]"
      />
      <p class="text-body-2 mb-4">
        Enter the recipient's name and email or mailing address to find their
        account.
      </p>
      <v-text-field
        v-model="email"
        label="Recipient email"
        type="email"
        maxlength="100"
        :rules="[emailRule]"
      />
      <v-checkbox
        v-if="method !== PurchaseDeliveryMethod.MAIL"
        v-model="useAddress"
        label="Use a mailing address to identify the recipient"
      />
      <template v-if="needsAddress">
        <v-text-field
          v-model="address.line1"
          label="Street address"
          :rules="[required]"
        />
        <v-text-field
          v-model="address.line2"
          label="Address line 2 (optional)"
        />
        <v-text-field
          v-model="address.line3"
          label="Address line 3 (optional)"
        />
        <v-text-field v-model="address.city" label="City" :rules="[required]" />
        <v-select
          v-model="address.country"
          label="Country"
          :items="Object.values(CountryCode)"
        />
        <v-select
          v-if="address.country === CountryCode.US"
          v-model="address.state"
          label="State"
          :items="Object.values(USStateCode)"
          :rules="[required]"
        />
        <v-text-field
          v-model="address.zip"
          label="Postal code"
          :rules="[required]"
        />
      </template>
    </template>
    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{
      error
    }}</v-alert>
    <v-btn
      variant="outlined"
      type="submit"
      :loading="busy || loading"
      :disabled="disabled || loading"
    >
      {{ submitText }}
    </v-btn>
  </v-form>
</template>
<script lang="ts" setup>
import { useMemberService } from "../../../cbi-generator/frontend/composables/useMemberFrontendService";
import type { ConfigureDelivery } from "../../../cbi-generator/generated/dto/ConfigureDelivery";
import { PurchaseDeliveryMethod } from "../../../cbi-generator/generated/dto/PurchaseDeliveryMethod";
import { PurchaseDeliveryTarget } from "../../../cbi-generator/generated/dto/PurchaseDeliveryTarget";
import { CountryCode } from "../../../cbi-generator/generated/dto/CountryCode";
import { USStateCode } from "../../../cbi-generator/generated/dto/USStateCode";
import type { VForm } from "vuetify/components";
import { errorMessage } from "@/composables/generated/contracts";
import { useAuthStore } from "@/stores/auth";

const props = defineProps<{
  submitText: string;
  loading?: boolean;
  disabled?: boolean;
  validatePurchase?: () => void;
}>();
const emit = defineEmits<{ submit: [delivery: ConfigureDelivery] }>();
const members = useMemberService();
const form = ref<VForm>();
const method = ref(PurchaseDeliveryMethod.EMAIL);
const target = ref(PurchaseDeliveryTarget.PURCHASER);
const first = ref("");
const last = ref("");
const email = ref("");
const useAddress = ref(false);
const address = reactive({
  line1: "",
  line2: "",
  line3: "",
  city: "",
  zip: "",
  country: CountryCode.US,
  state: null as USStateCode | null,
});
const needsAddress = computed(
  () => useAddress.value || method.value === PurchaseDeliveryMethod.MAIL,
);
const busy = ref(false);
const error = ref("");
const required = (value: unknown) =>
  !!String(value ?? "").trim() || "Required.";
const emailRule = (value: string) =>
  (!value.trim() &&
    method.value !== PurchaseDeliveryMethod.EMAIL &&
    needsAddress.value) ||
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()) ||
  "Enter a valid recipient email.";

async function submit() {
  if (
    busy.value ||
    props.loading ||
    props.disabled ||
    !(await form.value?.validate())?.valid
  )
    return;
  busy.value = true;
  error.value = "";
  try {
    props.validatePurchase?.();
    if (target.value === PurchaseDeliveryTarget.RECIPIENT)
      await useAuthStore().ensureProtoMember();
    const lookup =
      target.value === PurchaseDeliveryTarget.RECIPIENT
        ? (
            await members.lookupRecipientMember({
              first: first.value.trim(),
              last: last.value.trim(),
              email: email.value.trim() || null,
              address: needsAddress.value
                ? {
                    ...address,
                    state:
                      address.country === CountryCode.US ? address.state : null,
                    line2: address.line2 || null,
                    line3: address.line3 || null,
                  }
                : null,
            })
          ).lookup
        : null;
    emit("submit", {
      target: target.value,
      method: method.value,
      personLookup: lookup,
      targetPerson: null,
      targetAddress: null,
    });
  } catch (cause) {
    error.value = errorMessage(cause);
  } finally {
    busy.value = false;
  }
}
</script>
